import { DirtyFieldsType, GetOperations, Operation } from '@occc-app/offerHelpers/types';
import { makeDeleteOperation } from '@occc-app/offerHelpers/operations/makeDeleteOperation';
import {
  makeCreateRequiresVoucherAvailableForDiscountConstraintOperation,
  makeCreateRequiresVoucherConstraintOperation,
} from '@occc-app/offerHelpers/operations/constraints';
import { OfferFragment } from '@occc/graphql';
import { constraintHelper } from '@occc-app/offerHelpers/utils/constraintHelper';
import {
  getFulfillmentChannelConstraintOperations,
  getFulfillmentWindowOperations,
  getPaymentMethodConstraintOperations,
  getPreAuthConstraintOperations as getBasePreAuthConstraintOperations,
  getVoucherStackingConstraintOperations,
} from '@occc/feat-constraints';

import { ConfigureFormValues, AWARD_TYPES } from '../../../types';
import { constraintByType, offerSchemaByType } from '../constants';
import {
  getIsAwardTypeChanged,
  getIsSupportFulfillmentChannelConstraint,
  getIsSupportPaymentConstraint,
  getIsSupportPreAuthConstraint,
} from '../utils';

const filterOutVoucherConstraints = (constraints: OfferFragment['constraints']) => {
  return constraints.filter((constraint) => {
    return (
      constraint.__typename !== 'ConstraintRequiresVoucher' &&
      constraint.__typename !== 'ConstraintRequiresVoucherAvailableForDiscount' &&
      constraint.__typename !== 'ConstraintRequiresTransactionVoucher'
    );
  });
};

const getPaymentConstraintOperations = (
  offerId: string,
  offer: OfferFragment,
  values: ConfigureFormValues,
  dirtyFields: DirtyFieldsType,
) => {
  const isSupportPaymentConstraint = getIsSupportPaymentConstraint(values.awardType);
  if (!isSupportPaymentConstraint) {
    return [];
  }

  return getPaymentMethodConstraintOperations(offerId, offer, values, dirtyFields);
};

const getFulfillmentChannelOperations = (
  offerId: string,
  offer: OfferFragment,
  values: ConfigureFormValues,
) => {
  if (!getIsSupportFulfillmentChannelConstraint(values.awardType)) {
    return [];
  }

  return getFulfillmentChannelConstraintOperations(offerId, offer, values);
};

const getPreAuthConstraintOperations = (
  offer: OfferFragment,
  awardType: AWARD_TYPES | undefined,
) => {
  const isSupportPreAuthConstraint = getIsSupportPreAuthConstraint(awardType);
  if (!isSupportPreAuthConstraint) {
    return [];
  }

  return getBasePreAuthConstraintOperations(offer);
};

const getDeleteOperationsForNotSupportedConstraints = (
  award: OfferFragment,
  awardType?: AWARD_TYPES,
): Operation[] => {
  const operations: Operation[] = [];

  const supportedConstraints = awardType ? offerSchemaByType[awardType].constraints : [];

  /**
   * Voucher constraints should be removed as we handle them separately
   *
   * @see {@link getRequiresVoucherConstraintsDeleteOperations}
   */
  const constraints = filterOutVoucherConstraints(award.constraints);

  constraints.forEach((constraint) => {
    const isSupportedConstraint = supportedConstraints.some(
      (typename) => constraint.__typename === typename,
    );

    if (!isSupportedConstraint && 'id' in constraint) {
      operations.push(makeDeleteOperation('constraintDelete', constraint.id));
    }
  });

  return operations;
};

const getRequiresVoucherConstraintsDeleteOperations = (award: OfferFragment): Operation[] => {
  const operations: Operation[] = [];

  const requiresVoucherConstraint = award.constraints.find(constraintHelper.isRequiresVoucher);
  const requiresVoucherForDiscountConstraint = award.constraints.find(
    constraintHelper.isRequiresVoucherForDiscount,
  );
  const requiresTransactionVoucherConstraint = award.constraints.find(
    constraintHelper.isRequiresTransactionVoucher,
  );

  // According to "rules" on a BE side we have to delete all limits first before we will delete
  // requires voucher/requires voucher for discount constraints

  // Award Count limits are expected to be removed here:
  // 'LimitGlobalAwardCount', 'LimitGlobalAwardCountInTime', 'LimitMembershipAwardCount', 'LimitMembershipAwardCountInTime',
  award.limits.forEach((item) => {
    operations.push(makeDeleteOperation('limitDelete', item.id));
  });
  // Also, we need to make sure that all removed limits will be created again later if needed

  if (requiresVoucherConstraint) {
    operations.push(makeDeleteOperation('constraintDelete', requiresVoucherConstraint.id));
  }

  if (requiresVoucherForDiscountConstraint) {
    operations.push(
      makeDeleteOperation('constraintDelete', requiresVoucherForDiscountConstraint.id),
    );
  }

  if (requiresTransactionVoucherConstraint) {
    operations.push(
      makeDeleteOperation('constraintDelete', requiresTransactionVoucherConstraint.id),
    );
  }

  return operations;
};

const getRequiresVoucherConstraintsCreateOperations = (
  awardId: string,
  awardType?: AWARD_TYPES,
) => {
  const operations: Operation[] = [];

  if (!awardType) {
    return operations;
  }

  const constraintType = constraintByType[awardType];

  if (constraintType === 'ConstraintRequiresVoucher') {
    operations.push(makeCreateRequiresVoucherConstraintOperation(awardId));
  } else if (constraintType === 'ConstraintRequiresVoucherAvailableForDiscount') {
    operations.push(makeCreateRequiresVoucherAvailableForDiscountConstraintOperation(awardId));
  }

  return operations;
};

export const getConstraintOperations: GetOperations = (
  awardId,
  award,
  values: ConfigureFormValues,
  dirtyFields,
): Operation[] => {
  if (!award) {
    return [];
  }

  const operations: Operation[] = [];

  const deleteOperationsForNotSupportedConstraints = getDeleteOperationsForNotSupportedConstraints(
    award,
    values.awardType,
  );
  operations.push(...deleteOperationsForNotSupportedConstraints);

  if (getIsAwardTypeChanged(award, values)) {
    const requiresVoucherConstraintsDeleteOperations =
      getRequiresVoucherConstraintsDeleteOperations(award);
    const requiresVoucherConstraintsCreateOperations =
      getRequiresVoucherConstraintsCreateOperations(award.id, values.awardType);

    operations.push(
      ...requiresVoucherConstraintsDeleteOperations,
      ...requiresVoucherConstraintsCreateOperations,
    );
  }

  if (values.fulfillmentWindow) {
    const fulfillmentWindowConstraintOperations = getFulfillmentWindowOperations(
      awardId,
      values.fulfillmentWindow,
      dirtyFields,
    );

    operations.push(...fulfillmentWindowConstraintOperations);
  }

  const paymentConstraintOperations = getPaymentConstraintOperations(
    awardId,
    award,
    values,
    dirtyFields,
  );

  const preAuthConstraintOperations = getPreAuthConstraintOperations(award, values.awardType);

  const fulfillmentChannelOperations = getFulfillmentChannelOperations(awardId, award, values);

  if (values.awardType === AWARD_TYPES.FUEL_ROLLBACK) {
    operations.push(...getVoucherStackingConstraintOperations(awardId, award, values));
  }

  return [
    ...operations,
    ...paymentConstraintOperations,
    ...preAuthConstraintOperations,
    ...fulfillmentChannelOperations,
  ];
};
